import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale } from '@/i18n'

export type ProductKey =
  | 'healthExpenses'
  | 'housing'
  | 'civil'
  | 'abroad'
  | 'arrivingFrance'
  | 'healthTopUp'
  | 'regionalAura'
  | 'solidariteExam'

export const productSlugs: Record<ProductKey, Record<Locale, string>> = {
  healthExpenses:  { fr: 'depenses-sante',        en: 'health-expenses' },
  housing:         { fr: 'assurance-habitation',  en: 'home-insurance' },
  civil:           { fr: 'responsabilite-civile', en: 'civil-liability' },
  abroad:          { fr: 'etudier-a-letranger',   en: 'study-abroad' },
  arrivingFrance:  { fr: 'arrivee-en-france',     en: 'arriving-in-france' },
  healthTopUp:     { fr: 'renforcer-sante',       en: 'health-top-up' },
  regionalAura:    { fr: 'mutuelle-aura',         en: 'aura-health-plan' },
  solidariteExam:  { fr: 'solidarite-exam',       en: 'exam-insurance' },
}

export const PRODUCT_KEYS = Object.keys(productSlugs) as readonly ProductKey[]

export function productHref(key: ProductKey, locale: Locale = DEFAULT_LOCALE): string {
  const slug = productSlugs[key][locale]
  return locale === DEFAULT_LOCALE ? `/${slug}` : `/${locale}/${slug}`
}

/** Parcours d'assurance logement hébergé chez Léocare (hors site). */
export const HOUSING_QUOTE_HREF =
  'https://app.leocare.eu/fr/devis-assurance-en-ligne/logement-etudiant/ALEOSMERRA003/date-naissance?at_medium=online_&at_source=smerra&at_campaign=site_smerra&at_device=web&at_target=prosp&at_message_type=site&at_goal=a&at_product=mrh'

export type HomeProduct = {
  key: ProductKey
  /** Si défini : lien externe (nouvel onglet) au lieu du parcours interne. */
  externalHref?: string
}

/** Produits présentés sur la home, dans l'ordre d'affichage. Source de vérité
 *  partagée par la grille d'accueil (ChoicePanel) et le menu mobile (AppHeader). */
export const HOME_PRODUCTS: readonly HomeProduct[] = [
  { key: 'healthExpenses' },
  { key: 'housing', externalHref: HOUSING_QUOTE_HREF },
  { key: 'civil' },
  { key: 'abroad' },
  { key: 'arrivingFrance' },
  { key: 'healthTopUp' },
  { key: 'regionalAura' },
  { key: 'solidariteExam' },
]

/* ----------------------------------------------------------------
 * Steps & answers — URL = source de vérité.
 * Chaque réponse devient un segment de path, encodé selon la locale.
 * ----------------------------------------------------------------*/

export type StepKey = 'forWhom' | 'isStudent' | 'age' | 'internetProtection' | 'destination' | 'franceStay' | 'residency' | 'careFrequency' | 'opticalExpenses' | 'dentalNeeds' | 'extraReimbursements' | 'plan' | 'topUp' | 'crossSell'

/** Liste canonique des étapes (ordre de référence). La traversée réelle est
 *  conditionnelle et dépend du produit : voir nextStep / resolveSubscription. */
export const stepOrder: readonly StepKey[] = ['forWhom', 'isStudent', 'age', 'internetProtection', 'destination', 'franceStay', 'residency', 'careFrequency', 'opticalExpenses', 'dentalNeeds', 'extraReimbursements', 'plan', 'topUp', 'crossSell']

type StepOptionDef = {
  options: readonly string[]
  slugs: Record<string, Record<Locale, string>>
}

export const stepOptions: Record<StepKey, StepOptionDef> = {
  forWhom: {
    options: ['self', 'child'],
    slugs: {
      self:  { fr: 'moi',        en: 'me' },
      child: { fr: 'mon-enfant', en: 'my-child' },
    },
  },
  isStudent: {
    options: ['yes', 'no'],
    slugs: {
      yes: { fr: 'oui', en: 'yes' },
      no:  { fr: 'non', en: 'no' },
    },
  },
  // Les bornes d'âge varient selon "self" (18/30) ou "child" (15/30),
  // mais les 3 buckets restent identiques côté slugs/URL.
  age: {
    options: ['young', 'mid', 'senior'],
    slugs: {
      young:  { fr: 'jeune',         en: 'young'  },
      mid:    { fr: 'intermediaire', en: 'mid'    },
      senior: { fr: 'senior',        en: 'senior' },
    },
  },
  // Protection internet (responsabilite-civile) — dernière question de l'arbre
  // RC : oui/non alimente la recommandation (gérée côté back-end).
  internetProtection: {
    options: ['yes', 'no'],
    slugs: {
      yes: { fr: 'oui', en: 'yes' },
      no:  { fr: 'non', en: 'no'  },
    },
  },
  // Destination (etudier-a-letranger) — devoir de conseil : Europe ou hors Europe.
  destination: {
    options: ['europe', 'outsideEurope'],
    slugs: {
      europe:        { fr: 'en-europe',   en: 'in-europe'      },
      outsideEurope: { fr: 'hors-europe', en: 'outside-europe' },
    },
  },
  // Résidence en région AURA (mutuelle-aura) — non → sortie du parcours.
  residency: {
    options: ['yes', 'no'],
    slugs: {
      yes: { fr: 'oui', en: 'yes' },
      no:  { fr: 'non', en: 'no'  },
    },
  },
  // Durée du séjour en France (arrivee-en-france) — oriente le pack recommandé ;
  // « long » (> 8 mois) bascule vers la complémentaire santé (cf. composant).
  franceStay: {
    options: ['short', 'mid', 'long'],
    slugs: {
      short: { fr: '3-mois-max',  en: 'up-to-3-months' },
      mid:   { fr: '3-a-8-mois',  en: '3-to-8-months'  },
      long:  { fr: 'plus-8-mois', en: 'over-8-months'  },
    },
  },
  // Étape de recommandation — actuellement spécifique à "depenses-sante" :
  // fréquence de consultation de professionnels de santé.
  careFrequency: {
    options: ['often', 'little', 'no'],
    slugs: {
      often:  { fr: 'souvent', en: 'often'    },
      little: { fr: 'un-peu',  en: 'a-little' },
      no:     { fr: 'non',     en: 'no'       },
    },
  },
  // Dépenses en optique (depenses-sante).
  opticalExpenses: {
    options: ['high', 'low', 'none'],
    slugs: {
      high: { fr: 'elevees',     en: 'high' },
      low:  { fr: 'peu-elevees', en: 'low'  },
      none: { fr: 'nulles',      en: 'none' },
    },
  },
  // Besoins en soins dentaires (depenses-sante).
  dentalNeeds: {
    options: ['yes', 'yesProsthesis', 'no'],
    slugs: {
      yes:           { fr: 'oui',           en: 'yes'            },
      yesProsthesis: { fr: 'oui-protheses', en: 'yes-prosthesis' },
      no:            { fr: 'non',           en: 'no'             },
    },
  },
  // Remboursements complémentaires bien-être (depenses-sante).
  extraReimbursements: {
    options: ['yes', 'no'],
    slugs: {
      yes: { fr: 'oui', en: 'yes' },
      no:  { fr: 'non', en: 'no'  },
    },
  },
  // Offre retenue à l'écran résultats. Santé : confort/allRisk/essential/minimal.
  // RC : security/indispensable. Étranger : packEurope/packMonde. Slugs distincts.
  plan: {
    options: ['confort', 'allRisk', 'essential', 'minimal', 'security', 'indispensable', 'packEurope', 'packMonde', 'packFranceGlobal', 'packFranceConfort', 'tonic', 'primordiale', 'renforcee', 'exam'],
    slugs: {
      confort:           { fr: 'confort',         en: 'comfort'        },
      allRisk:           { fr: 'tous-risques',    en: 'all-risk'       },
      essential:         { fr: 'essentielle',     en: 'essential'      },
      minimal:           { fr: 'minimale',        en: 'minimal'        },
      security:          { fr: 'securite',        en: 'security'       },
      indispensable:     { fr: 'indispensable',   en: 'indispensable'  },
      packEurope:        { fr: 'pack-europe',     en: 'europe-pack'    },
      packMonde:         { fr: 'pack-monde',      en: 'world-pack'     },
      packFranceGlobal:  { fr: 'pack-global',     en: 'global-pack'    },
      packFranceConfort: { fr: 'pack-confort',    en: 'comfort-pack'   },
      tonic:             { fr: 'tonic-etudes',    en: 'tonic-studies'  },
      primordiale:       { fr: 'primordiale',     en: 'primordiale'    },
      renforcee:         { fr: 'renforcee',       en: 'reinforced'     },
      exam:              { fr: 'examen',          en: 'exam'           },
    },
  },
  // Surcomplémentaire « Tonic-Études » (depenses-sante) — proposée uniquement
  // si « oui » à la dernière question (remboursements bien-être) et formule
  // Essentielle/Minimale, juste avant le cross-sell.
  topUp: {
    options: ['subscribe', 'skip'],
    slugs: {
      subscribe: { fr: 'souscrire', en: 'subscribe' },
      skip:      { fr: 'passer',    en: 'skip'      },
    },
  },
  // Cross-sell : assurance complémentaire proposée après le choix de la formule
  // (depenses-sante) — 2 add-ons possibles ou « non merci ».
  crossSell: {
    options: ['security', 'indispensable', 'none'],
    slugs: {
      security:      { fr: 'securite',      en: 'security'  },
      indispensable: { fr: 'indispensable', en: 'essential' },
      none:          { fr: 'non-merci',     en: 'no-thanks' },
    },
  },
}

export function answerSlug(step: StepKey, value: string, locale: Locale): string {
  return stepOptions[step].slugs[value]?.[locale] ?? value
}

export function answerFromSlug(step: StepKey, slug: string, locale: Locale): string | null {
  const opts = stepOptions[step]
  for (const opt of opts.options) {
    if (opts.slugs[opt]?.[locale] === slug) return opt
  }
  return null
}

/* ----------------------------------------------------------------
 * Traversée du parcours (arbre de décision).
 * L'ordre des étapes dépend des réponses : la surcomplémentaire « topUp »
 * n'est proposée que si « oui » à la dernière question (remboursements
 * bien-être) et formule Essentielle/Minimale. On marche donc d'étape en étape
 * plutôt que par position fixe dans l'URL.
 * ----------------------------------------------------------------*/
export const FIRST_STEP: StepKey = 'forWhom'

/** Arbre de décision « responsabilité civile » :
 *  pour qui → étudiant ? → âge → protection internet → (reco back-end).
 *  Les sorties (non-étudiant / mineur / +30 ans) sont gérées à l'affichage
 *  (cf. showExit dans SubscriptionStartCard), pas par la traversée d'étapes. */
function nextStepCivil(step: StepKey): StepKey | null {
  switch (step) {
    case 'forWhom':            return 'isStudent'
    case 'isStudent':          return 'age'
    case 'age':                return 'internetProtection'
    case 'internetProtection': return 'plan'   // écran résultats = choix de l'offre
    case 'plan':               return null
    default:                   return null
  }
}

/** Arbre « je vais partir à l'étranger » (etudier-a-letranger) :
 *  pour qui → étudiant ? → âge (borne 34 ans) → destination → (suite à venir). */
function nextStepAbroad(step: StepKey): StepKey | null {
  switch (step) {
    case 'forWhom':     return 'isStudent'
    case 'isStudent':   return 'age'
    case 'age':         return 'destination'
    case 'destination': return 'plan'   // écran résultats = pack recommandé
    case 'plan':        return null
    default:            return null
  }
}

/** Arbre « ma mutuelle Auvergne-Rhône-Alpes » (mutuelle-aura) : pas de question
 *  étudiant. pour qui → âge → résidence en région → optique → dentaire → produit.
 *  Sorties (mineur / +30 / non-résident) gérées à l'affichage (showExit). */
function nextStepRegionalAura(step: StepKey): StepKey | null {
  switch (step) {
    case 'forWhom':         return 'age'
    case 'age':             return 'residency'
    case 'residency':       return 'opticalExpenses'
    case 'opticalExpenses': return 'dentalNeeds'
    case 'dentalNeeds':     return 'plan'       // écran résultats = Primordiale / Renforcée
    case 'plan':            return 'crossSell'  // cross-sell assurance
    case 'crossSell':       return null
    default:                return null
  }
}

/** Arbre « je renforce mes remboursements de santé » (renforcer-sante) :
 *  pour qui → étudiant ? → âge → Tonic-Études. Seul le mineur sort (l'étudiant
 *  comme le non-étudiant, et tous les âges ≥ 18/15, poursuivent). */
function nextStepHealthTopUp(step: StepKey): StepKey | null {
  switch (step) {
    case 'forWhom':   return 'isStudent'
    case 'isStudent': return 'age'
    case 'age':       return 'plan'       // écran résultats = Tonic-Études
    case 'plan':      return 'crossSell'  // cross-sell assurance (Sécurité/Indispensable)
    case 'crossSell': return null
    default:          return null
  }
}

/** Arbre « Solidarité Exam » (solidarite-exam) : mêmes questions de gating que
 *  la complémentaire santé, mais l'arbre back-end (DecisionTree::SolidariteExam)
 *  renvoie toujours l'unique bundle Solidarité exam.
 *  pour qui → étudiant ? → âge → offre (bundle unique). Les sorties (mineur…)
 *  sont gérées à l'affichage (showExit), comme depenses-sante. */
function nextStepSolidariteExam(step: StepKey): StepKey | null {
  switch (step) {
    case 'forWhom':   return 'isStudent'
    case 'isStudent': return 'age'
    case 'age':       return 'plan'   // écran résultats = offre Solidarité exam
    case 'plan':      return null
    default:          return null
  }
}

/** Arbre « j'arrive en France » (arrivee-en-france) :
 *  pour qui → étudiant ? → âge → durée du séjour → pack recommandé.
 *  Le cas « > 8 mois » bascule vers la complémentaire santé (redirection gérée
 *  dans le composant), donc il n'apparaît pas ici. */
function nextStepArrivingFrance(step: StepKey): StepKey | null {
  switch (step) {
    case 'forWhom':    return 'isStudent'
    case 'isStudent':  return 'age'
    case 'age':        return 'franceStay'
    case 'franceStay': return 'plan'   // écran résultats = Pack Global / Confort
    case 'plan':       return null
    default:           return null
  }
}

/** Arbre « dépenses santé » (parcours historique, par défaut). */
function nextStepHealth(step: StepKey, answers: Record<string, string>): StepKey | null {
  switch (step) {
    case 'forWhom':             return 'isStudent'
    case 'isStudent':           return 'age'
    case 'age':                 return 'careFrequency'
    case 'careFrequency':       return 'opticalExpenses'
    case 'opticalExpenses':     return 'dentalNeeds'
    case 'dentalNeeds':         return 'extraReimbursements'
    case 'extraReimbursements': return 'plan'
    case 'plan':
      // Surcomplémentaire « Tonic-Études » proposée uniquement si l'utilisateur
      // a répondu « oui » à la dernière question (remboursements bien-être) ET
      // a retenu une formule Essentielle ou Minimale.
      return answers.extraReimbursements === 'yes' &&
        (answers.plan === 'essential' || answers.plan === 'minimal')
        ? 'topUp'
        : 'crossSell'
    case 'topUp':               return 'crossSell'
    case 'crossSell':           return null
    default:                    return null
  }
}

/** Étape suivant `step` selon le produit et les réponses déjà collectées,
 *  ou null si fin de parcours. */
export function nextStep(
  step: StepKey,
  answers: Record<string, string>,
  product?: ProductKey,
): StepKey | null {
  if (product === 'civil') return nextStepCivil(step)
  if (product === 'abroad') return nextStepAbroad(step)
  if (product === 'arrivingFrance') return nextStepArrivingFrance(step)
  if (product === 'healthTopUp') return nextStepHealthTopUp(step)
  if (product === 'regionalAura') return nextStepRegionalAura(step)
  if (product === 'solidariteExam') return nextStepSolidariteExam(step)
  return nextStepHealth(step, answers)
}

export type ResolvedSubscription = {
  answers: Record<string, string>
  /** Étapes traversées (répondues + étape courante non répondue). */
  sequence: StepKey[]
  /** Prochaine étape à répondre, ou null si le parcours est terminé. */
  currentStepKey: StepKey | null
}

/** Décode les slugs d'URL en réponses en suivant l'arbre de décision du produit. */
export function resolveSubscription(
  slugs: readonly string[],
  locale: Locale,
  product?: ProductKey,
): ResolvedSubscription {
  const answers: Record<string, string> = {}
  const sequence: StepKey[] = []
  let step: StepKey | null = FIRST_STEP
  let i = 0
  while (step) {
    sequence.push(step)
    if (i >= slugs.length) return { answers, sequence, currentStepKey: step }
    const value = answerFromSlug(step, slugs[i], locale)
    if (value) answers[step] = value
    i++
    step = nextStep(step, answers, product)
  }
  return { answers, sequence, currentStepKey: null }
}

/** Nombre total d'étapes du parcours projeté à partir des réponses connues
 *  (les branches non encore décidées prennent leur chemin par défaut). */
export function projectedStepCount(answers: Record<string, string>, product?: ProductKey): number {
  let step: StepKey | null = FIRST_STEP
  let count = 0
  while (step) {
    count++
    step = nextStep(step, answers, product)
  }
  return count
}

export function subscriptionHref(
  product: ProductKey,
  answerSlugs: readonly string[],
  locale: Locale = DEFAULT_LOCALE,
): string {
  const base = productHref(product, locale)
  return answerSlugs.length ? `${base}/${answerSlugs.join('/')}` : base
}

export function stepIndex(step: StepKey): number {
  return stepOrder.indexOf(step) + 1
}

/* ----------------------------------------------------------------
 * Slugs des pages statiques localisées (hors produits). Indexés par clé
 * logique → table locale → slug. Sert à `swapLocaleInPath` pour basculer
 * /merci ↔ /en/thank-you correctement.
 * ----------------------------------------------------------------*/
export const staticPageSlugs: Record<string, Record<Locale, string>> = {
  thankYou:       { fr: 'merci',                en: 'thank-you' },
  paymentLink:    { fr: 'lien-de-paiement',     en: 'payment-link' },
  login:          { fr: 'connexion',            en: 'login' },
  forgotPassword: { fr: 'mot-de-passe-oublie',  en: 'forgot-password' },
  resetPassword:  { fr: 'initialisation-mot-de-passe', en: 'password-initialization' },
  account:        { fr: 'espace-personnel',     en: 'my-account' },
}

export type StaticPageKey = keyof typeof staticPageSlugs

/** URL localisée d'une page statique (ex: login → /connexion · /en/login). */
export function staticPageHref(key: StaticPageKey, locale: Locale = DEFAULT_LOCALE): string {
  const slug = staticPageSlugs[key][locale]
  return locale === DEFAULT_LOCALE ? `/${slug}` : `/${locale}/${slug}`
}

/** Sous-pages de l'espace personnel (clé = section, comme la nav de la sidebar).
 *  Base : /espace-personnel (FR) · /en/my-account (EN). */
export const accountSectionSlugs = {
  contracts:   { fr: 'mes-contrats',          en: 'contracts' },
  card:        { fr: 'carte-tiers-payant',    en: 'payment-card' },
  documents:   { fr: 'mes-documents',         en: 'documents' },
  reimbursements: { fr: 'mes-remboursements', en: 'reimbursements' },
  subscribe:   { fr: 'souscrire-un-produit',  en: 'subscribe' },
  info:        { fr: 'mes-informations',   en: 'details' },
  credentials: { fr: 'mes-identifiants',   en: 'credentials' },
  payment:     { fr: 'mon-rib-de-remboursement', en: 'refund-bank-details' },
  contact:     { fr: 'nous-contacter',     en: 'contact' },
} as const satisfies Record<string, Record<Locale, string>>

export type AccountSection = keyof typeof accountSectionSlugs

/** URL localisée d'une sous-page de l'espace perso (ou 'dashboard' = la racine). */
export function accountHref(section: AccountSection | 'dashboard', locale: Locale = DEFAULT_LOCALE): string {
  const base = staticPageHref('account', locale)
  return section === 'dashboard' ? base : `${base}/${accountSectionSlugs[section][locale]}`
}

/* ----------------------------------------------------------------
 * Bascule de locale qui traduit produit + slugs de réponses.
 * ----------------------------------------------------------------*/
export function swapLocaleInPath(pathname: string, target: Locale): string {
  const segs = pathname.split('/').filter(Boolean)
  const first = segs[0]
  const hasPrefix = (SUPPORTED_LOCALES as readonly string[]).includes(first)
  const currentLocale: Locale = hasPrefix ? (first as Locale) : DEFAULT_LOCALE
  const rest = hasPrefix ? segs.slice(1) : segs

  if (rest.length >= 1) {
    const productSlug = rest[0]
    let productKey: ProductKey | null = null
    for (const key of PRODUCT_KEYS) {
      if (productSlugs[key][currentLocale] === productSlug) {
        productKey = key
        break
      }
    }
    if (productKey) {
      const answerSlugs = rest.slice(1)
      // Résoudre avec l'arbre DU PRODUIT (sinon les étapes spécifiques —
      // franceStay, destination, packs… — sont mal mappées et non traduites).
      const { sequence } = resolveSubscription(answerSlugs, currentLocale, productKey)
      const targetAnswerSlugs = answerSlugs.map((slug, i) => {
        const stepKey = sequence[i]
        if (!stepKey) return slug
        const value = answerFromSlug(stepKey, slug, currentLocale)
        return value ? answerSlug(stepKey, value, target) : slug
      })
      return subscriptionHref(productKey, targetAnswerSlugs, target)
    }

    // Page statique localisée (ex: /merci ↔ /en/thank-you).
    for (const [, slugs] of Object.entries(staticPageSlugs)) {
      if (slugs[currentLocale] === productSlug) {
        const targetSlug = slugs[target]
        return target === DEFAULT_LOCALE ? `/${targetSlug}` : `/${target}/${targetSlug}`
      }
    }

    // Page statique localisée (ex: /merci ↔ /en/thank-you).
    for (const [, slugs] of Object.entries(staticPageSlugs)) {
      if (slugs[currentLocale] === productSlug) {
        const targetSlug = slugs[target]
        return target === DEFAULT_LOCALE ? `/${targetSlug}` : `/${target}/${targetSlug}`
      }
    }
  }

  const restPath = rest.length ? '/' + rest.join('/') : '/'
  return target === DEFAULT_LOCALE ? restPath : `/${target}${restPath === '/' ? '/' : restPath}`
}
